import React, { useState } from 'react';
import { Volume2, RotateCw } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { ExerciseBanner } from './ExerciseBanner';
import { resolveFontSize } from '../config/fontSizes';

/**
 * SimpleFlashcard - Дизайн карточек по концепту
 * Одна сторона: бейдж уровня, немецкое слово, грамматика, перевод, пример, кнопки
 * Обратная сторона (по клику): оригинал + перевод + кнопки
 */
export const SimpleFlashcard = ({ 
  pair, 
  onAudio,
  onKnow,
  onDontKnow,
  topic
}) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const { t } = useLanguage();


  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleAudioClick = (e) => {
    e.stopPropagation();
    onAudio(pair.german);
  };

  const handleKnowClick = (e) => {
    e.stopPropagation();
    onKnow();
  };

  const handleDontKnowClick = (e) => {
    e.stopPropagation();
    onDontKnow();
  };

  return (
    <div className="fc-wrapper">
      <div 
        className={`fc-inner ${isFlipped ? 'flipped' : ''}`}
        onClick={handleFlip}
      >
        {/* ── ЛИЦЕВАЯ СТОРОНА ── */}
        <div className="fc-face">
          <div className="fc-content">
            {/* Badge */}
            <div className="fc-badge">
              {topic?.level || 'A2'} · {t('topicLabel')} {topic?.germanTitle || t('topic')}
            </div>

            {/* Body */}
            <div className="fc-body">
              <h2
                className="fc-word"
                style={{
                  color: pair.germanTextColor,
                  ...(resolveFontSize(pair.germanFontSize) ? { fontSize: resolveFontSize(pair.germanFontSize) } : {})
                }}
              >
                {pair.german}
              </h2>

              {pair.germanDescription && (
                <p
                  className="fc-grammar"
                  style={{
                    color: pair.germanDescriptionColor,
                    ...(resolveFontSize(pair.germanDescriptionFontSize) ? { fontSize: resolveFontSize(pair.germanDescriptionFontSize) } : {})
                  }}
                >
                  {pair.germanDescription}
                </p>
              )}

              {pair.russianDescription && (
                  // Translation description should only appear on the back side; omit from front
                  null
              )}
            </div>

            {/* Actions */}
            <div className="fc-actions">
              <button onClick={handleAudioClick} className="fc-audio-btn" title={t('listen') || 'Слушать'}>
                <Volume2 size={22} />
              </button>
              <button onClick={handleDontKnowClick} className="fc-btn-dont-know">
                ✗ {t('dontKnow') || 'Не знаю'}
              </button>
              <button onClick={handleKnowClick} className="fc-btn-know">
                ✓ {t('know') || 'Знаю'}
              </button>
            </div>
          </div>
        </div>

        {/* ── ОБРАТНАЯ СТОРОНА ── */}
        <div className="fc-face fc-back">
          <div className="fc-content">
            <div className="fc-body" style={{ gap: 16 }}>
              <p className="fc-back-label">{t('original') || 'Оригинал'}</p>
              <h3
                className="fc-word"
                style={{
                  color: pair.germanTextColor,
                  ...(resolveFontSize(pair.germanFontSize) ? { fontSize: resolveFontSize(pair.germanFontSize) } : { fontSize: 'clamp(36px, 6vw, 52px)' })
                }}
              >
                {pair.german}
              </h3>
              {pair.germanDescription && (
                <p
                  className="fc-grammar"
                  style={{
                    color: pair.germanDescriptionColor,
                    ...(resolveFontSize(pair.germanDescriptionFontSize) ? { fontSize: resolveFontSize(pair.germanDescriptionFontSize) } : {})
                  }}
                >
                  {pair.germanDescription}
                </p>
              )}

              <div className="fc-divider" />

              <p className="fc-back-label">{t('translation') || 'Перевод'}</p>
              <h3
                style={{
                  fontFamily: "'DM Serif Display', serif",
                  color: pair.russianTextColor || 'rgba(245,244,240,0.7)',
                  fontWeight: 700,
                  ...(resolveFontSize(pair.russianFontSize) ? { fontSize: resolveFontSize(pair.russianFontSize) } : { fontSize: 'clamp(28px, 5vw, 40px)' })
                }}
              >
                {pair.english}
              </h3>

              {pair.russianDescription && (
                <div className="fc-example" style={{ marginTop: 16 }}>
                  <p className="fc-example-de" style={{ color: pair.russianDescriptionColor }}>{pair.russianDescription}</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="fc-actions">
              <button onClick={handleDontKnowClick} className="fc-btn-dont-know">
                ✗ {t('dontKnow') || 'Не знаю'}
              </button>
              <button onClick={handleKnowClick} className="fc-btn-know">
                ✓ {t('know') || 'Знаю'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * ResultsScreen - Экран с результатами упражнения
 */
export const ResultsScreen = ({ 
  know = 0,
  dontKnow = 0,
  onRestart,
  onRestartKnown,
  onRestartUnknown,
  onBack,
  topic,
  exercise
}) => {
  const total = know + dontKnow;
  const percentage = total > 0 ? (know / total) * 100 : 0;
  const { t } = useLanguage();

  return (
    <div className="results-page">
      <div className="results-container">
        <div className="results-header">
          <h1 className="results-title">🎉 {t('finish')}!</h1>
          <p className="results-subtitle">{topic?.germanTitle} - {exercise?.title}</p>
        </div>

        <div className="results-stats-grid">
          <div className="results-stat-card correct">
            <div className="results-stat-number">{know}</div>
            <div className="results-stat-label">{t('cardsKnow')}</div>
            <div className="results-stat-pct">{total > 0 ? (know / total * 100).toFixed(0) : 0}% {t('ofAll')}</div>
            {know > 0 && (
              <button onClick={onRestartKnown} className="results-stat-btn">
                <RotateCw size={16} /> {t('repeatKnown')}
              </button>
            )}
          </div>
          <div className="results-stat-card incorrect">
            <div className="results-stat-number">{dontKnow}</div>
            <div className="results-stat-label">{t('cardsDontKnow')}</div>
            <div className="results-stat-pct">{total > 0 ? (dontKnow / total * 100).toFixed(0) : 0}% {t('ofAll')}</div>
            {dontKnow > 0 && (
              <button onClick={onRestartUnknown} className="results-stat-btn">
                <RotateCw size={16} /> {t('repeatUnknown')}
              </button>
            )}
          </div>
        </div>

        <div className="results-progress-section">
          <div className="results-progress-header">
            <span className="results-progress-label">{t('progress')}:</span>
            <span className="results-progress-value">{Math.round(percentage)}%</span>
          </div>
          <div className="results-progress-track">
            <div className="results-progress-fill" style={{ width: `${percentage}%` }} />
          </div>
        </div>

        <div className="results-detail-card">
          <h3 className="results-detail-title">📊 {t('total')}:</h3>
          <div className="results-detail-row">
            <span className="results-detail-label">{t('allCards')}:</span>
            <span className="results-detail-value">{total}</span>
          </div>
          <div className="results-detail-row" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: 12 }}>
            <span className="results-detail-label">{t('learned')}:</span>
            <span className="results-detail-value" style={{ color: 'var(--mint)' }}>{know}</span>
          </div>
          <div className="results-detail-row">
            <span className="results-detail-label">{t('needRepeat')}:</span>
            <span className="results-detail-value" style={{ color: 'var(--coral)' }}>{dontKnow}</span>
          </div>
        </div>

        <div className="results-actions">
          <button onClick={onRestart} className="results-btn-primary">
            <RotateCw size={20} /> {t('repeatAll')}
          </button>
          <button onClick={onBack} className="results-btn-secondary">
            🚪 {t('exit')}
          </button>
        </div>

        <div className="results-tip">
          💡 <strong>{t('tip')}:</strong> {t('reviewUnknownCards')}!
        </div>
      </div>
    </div>
  );
};

/**
 * FlashcardExercise - Полное упражнение с флешкартами
 */
export const FlashcardExercise = ({ 
  topic, 
  exercise, 
  onAnswer,
  onBack,
  onExit,
  showResultsScreen = true,
  currentExerciseIndex = 0,
  totalExercises = 1
}) => {
  const { t } = useLanguage();
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [knowCards, setKnowCards] = useState([]);
  const [dontKnowCards, setDontKnowCards] = useState([]);
  const [isFinished, setIsFinished] = useState(false);
  const [filteredPairs, setFilteredPairs] = useState(null); // null = все, 'know' = известные, 'dontKnow' = неизвестные
  const [originalKnowCards, setOriginalKnowCards] = useState([]); // сохраняем оригинальные результаты
  const [originalDontKnowCards, setOriginalDontKnowCards] = useState([]);

  // После миграции Sequelize pairs находится на верхнем уровне
  const pairs = exercise?.pairs || [];
  
  // Проверка на наличие данных
  if (!exercise || !pairs || pairs.length === 0) {
    return (
      <div className="exercise-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: 'rgba(245,244,240,0.5)', fontSize: 20, marginBottom: 16 }}>
            {t?.exerciseNotFound || 'Упражнение не найдено или не содержит данных'}
          </p>
          <button onClick={onBack} className="modern-button-primary">
            {t?.back || 'Назад'}
          </button>
        </div>
      </div>
    );
  }
  
  // Создаем массив индексов для активных карточек
  const activeIndices = filteredPairs === 'know' 
    ? originalKnowCards
    : filteredPairs === 'dontKnow'
    ? originalDontKnowCards
    : pairs.map((_, idx) => idx);
  
  // Определяем какие карточки показывать
  const activePairs = activeIndices.map(idx => pairs[idx]);
  
  const currentCard = activePairs[currentCardIndex];
  const currentOriginalIndex = activeIndices[currentCardIndex]; // индекс в оригинальном массиве

  const handleAudio = (text) => {
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'de-DE';
      utterance.rate = 0.85;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
      
      // Получаем список доступных голосов
      const voices = speechSynthesis.getVoices();
      
      // Приоритет 1: Microsoft Katja Online (Natural) — самый естественный
      let germanVoice = voices.find(voice => 
        voice.name.includes('Katja') && voice.name.includes('Online')
      );
      
      // Приоритет 2: Любой Microsoft Online/Natural немецкий голос
      if (!germanVoice) {
        germanVoice = voices.find(voice => 
          voice.lang.startsWith('de') && voice.name.includes('Online')
        );
      }
      
      // Приоритет 3: Любой Microsoft немецкий голос
      if (!germanVoice) {
        germanVoice = voices.find(voice => 
          voice.lang.startsWith('de') && voice.name.toLowerCase().includes('microsoft')
        );
      }
      
      // Приоритет 4: Google немецкий голос
      if (!germanVoice) {
        germanVoice = voices.find(voice => 
          voice.lang.startsWith('de') && voice.name.toLowerCase().includes('google')
        );
      }
      
      // Приоритет 5: Любой немецкий голос
      if (!germanVoice) {
        germanVoice = voices.find(voice => voice.lang.startsWith('de'));
      }
      
      // Устанавливаем найденный голос
      if (germanVoice) {
        utterance.voice = germanVoice;
      }
      
      speechSynthesis.speak(utterance);
    }
  };

  const handleKnow = () => {
    const newKnowCards = knowCards.includes(currentOriginalIndex)
      ? knowCards 
      : [...knowCards, currentOriginalIndex];
    setKnowCards(newKnowCards);

    goToNextCard(newKnowCards, dontKnowCards);
  };

  const handleDontKnow = () => {
    const newDontKnowCards = dontKnowCards.includes(currentOriginalIndex)
      ? dontKnowCards
      : [...dontKnowCards, currentOriginalIndex];
    setDontKnowCards(newDontKnowCards);

    goToNextCard(knowCards, newDontKnowCards);
  };

  const goToNextCard = (nextKnowCards = knowCards, nextDontKnowCards = dontKnowCards) => {
    if (currentCardIndex < activePairs.length - 1) {
      setCurrentCardIndex(currentCardIndex + 1);
    } else {
      // Все карточки пройдены
      if (showResultsScreen) {
        // Показиваем экран результатов
        setIsFinished(true);
        
        // Сохраняем оригинальные результаты только при первом прохождении всех карточек
        if (filteredPairs === null) {
          setOriginalKnowCards(nextKnowCards);
          setOriginalDontKnowCards(nextDontKnowCards);
        }
        
        if (onAnswer) {
          onAnswer({
            know: nextKnowCards.length,
            dontKnow: nextDontKnowCards.length,
            total: pairs.length
          });
        }
      } else {
        if (onAnswer && exercise?.id) {
          onAnswer({
            exerciseId: exercise.id,
            flashcardSummary: {
              correct: nextKnowCards.length,
              incorrect: nextDontKnowCards.length,
              correctIndices: nextKnowCards,
              incorrectIndices: nextDontKnowCards
            }
          });
        }

        onBack();
      }
    }
  };

  const handleRestart = () => {
    setCurrentCardIndex(0);
    setKnowCards([]);
    setDontKnowCards([]);
    setIsFinished(false);
    setFilteredPairs(null); // Повторить все
    setOriginalKnowCards([]);
    setOriginalDontKnowCards([]);
  };

  const handleRestartKnown = () => {
    setCurrentCardIndex(0);
    setKnowCards([]);
    setDontKnowCards([]);
    setIsFinished(false);
    setFilteredPairs('know'); // Повторить только известные
  };

  const handleRestartUnknown = () => {
    setCurrentCardIndex(0);
    setKnowCards([]);
    setDontKnowCards([]);
    setIsFinished(false);
    setFilteredPairs('dontKnow'); // Повторить только неизвестные
  };

  // Экран результатов (только если showResultsScreen = true)
  if (isFinished && showResultsScreen) {
    return (
      <ResultsScreen
        know={knowCards.length}
        dontKnow={dontKnowCards.length}
        onRestart={handleRestart}
        onRestartKnown={handleRestartKnown}
        onRestartUnknown={handleRestartUnknown}
        onBack={onBack}
        topic={topic}
        exercise={exercise}
      />
    );
  }

  const progressPct = totalExercises > 0 ? ((currentExerciseIndex + 1) / totalExercises) * 100 : 0;

  // Экран с карточками
  return (
    <div className="exercise-page">
      {/* Unified Banner with progress bar */}
      {showResultsScreen === false && (
        <div style={{ margin: '0 auto 32px', maxWidth: 1000, padding: '0 24px' }}>
          <ExerciseBanner
            currentIndex={currentExerciseIndex}
            totalExercises={totalExercises}
            correct={knowCards.length}
            incorrect={dontKnowCards.length}
            onExit={onExit}
            showProgress={true}
          />
          {/* Progress bar */}
          <div className="exercise-progress-row" style={{ marginTop: 8 }}>
            <span className="exercise-progress-label">{t('lessonProgress')}:</span>
            <div className="exercise-progress-track">
              <div className="exercise-progress-fill" style={{ width: `${progressPct}%` }} />
            </div>
            <span className="exercise-progress-text">{currentExerciseIndex + 1} / {totalExercises}</span>
          </div>
        </div>
      )}

      {/* Основная карточка */}
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '48px 24px' }}>
        {currentCard && (
          <div className="fc-swipe-in" key={currentCardIndex}>
            <SimpleFlashcard
              pair={currentCard}
              onAudio={handleAudio}
              onKnow={handleKnow}
              onDontKnow={handleDontKnow}
              topic={topic}
            />
          </div>
        )}
      </div>

    </div>
  );
};

export default FlashcardExercise;


